import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { addFactChecking, getFactChecking } from "../../HTTP/articles";
import { fetchUserTokens } from "../../HTTP/subscription";

export const FactChecking = ({ articleLink, setShowPopUpFactChecking }) => {
  const [factChecking, setFactChecking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [tokensLoading, setTokensLoading] = useState(true); // ← Nouvel état
  const language = sessionStorage.getItem("currentlanguage");
  const [userTokens, setUserTokens] = useState(null);
  const user = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    const fetchFactChecking = async () => {
      try {
        const res = await getFactChecking(articleLink);
        setFactChecking(res);
      } catch (error) {
        console.error("Error fetching fact checking:", error);
      }
    };

    const fetchTokensUser = async () => {
      try {
        setTokensLoading(true); // ← Début du chargement des tokens
        const response = await fetchUserTokens(user.id);
        if (response) {
          setUserTokens(response.tokens);
          console.log("User tokens fetched:", response); 
        } else {
          setUserTokens(0);
        }
      } catch (error) {
        console.error("Error fetching user tokens:", error);
        setUserTokens(0);
      } finally {
        setTokensLoading(false); // ← Fin du chargement des tokens
      }
    }

    // Exécuter les deux appels en parallèle
    Promise.all([fetchTokensUser(), fetchFactChecking()])
      .finally(() => {
        setLoading(false); // ← Tout est fini
      });

  }, [articleLink, language]);

  // Afficher un loader pendant le chargement initial
  if (loading || tokensLoading) {
    const content = (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-gray-800 text-white rounded-lg shadow-md overflow-hidden w-96 p-4">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
          </div>
          <p className="text-center mt-4">Chargement en cours...</p>
        </div>
      </div>
    );
    return createPortal(content, document.body);
  }

  // Fonction pour générer un nouveau fact-checking
  const generateFactChecking = async () => {
    setIsGenerating(true);
    try {
      const newFactCheck = await addFactChecking(articleLink);
      setFactChecking(newFactCheck);
      console.log("Fact-checking généré:", newFactCheck);
      
      // Mettre à jour les tokens après génération
      const updatedTokens = await fetchUserTokens(user.id);
      if (updatedTokens) {
        setUserTokens(updatedTokens.tokens);
      }
    } catch (error) {
      console.error("Erreur lors de la génération du fact-checking:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  // Afficher le fact-checking s'il existe
  if (factChecking) {
    const content = (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-zemusbis text-white rounded-lg shadow-md overflow-hidden ml-10 mr-10">
          <div className="flex flex-col">
            <div className="p-4">
              <h3 className="text-lg font-semibold mb-2">Vérification des faits</h3>
              <div className="bg-white text-black p-3 rounded mb-4 max-h-60 overflow-auto">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    a: ({ node, ...props }) => (
                      <a {...props} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                        {props.children}
                      </a>
                    ),
                    table: ({ node, ...props }) => (
                      <table {...props} className="min-w-full border-collapse text-sm" />
                    ),
                    th: ({ node, ...props }) => (
                      <th {...props} className="border border-gray-300 bg-gray-100 px-3 py-2 text-left font-semibold" />
                    ),
                    td: ({ node, ...props }) => (
                      <td {...props} className="border border-gray-300 px-3 py-2 align-top" />
                    ),
                  }}
                >
                  {factChecking.content}
                </ReactMarkdown>
              </div>
              <button
                onClick={() => setShowPopUpFactChecking(false)}
                className="bg-zemushover text-gray-500 font-bold py-2 px-4 rounded w-full"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
    );
    return createPortal(content, document.body);
  }

  // Vérifier si l'utilisateur a des tokens (maintenant on est sûr que userTokens est défini)
  const hasTokens = userTokens && userTokens > 0;

  // Si aucun fact-checking n'existe, afficher l'option pour en générer un nouveau
  const content = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-zemusbis text-white rounded-lg shadow-md overflow-hidden w-96 p-4">
        <h3 className="text-lg font-semibold mb-2 text-center">Vérification des faits</h3>

        {/* Afficher le nombre de tokens disponibles */}
        <p className="text-sm text-gray-400 mb-4 text-center">
          Tokens disponibles: {userTokens || 0}
        </p>
        
        <div className="flex gap-2">
          {isGenerating ? (
            <button
              disabled
              className="bg-blue-500 opacity-70 text-white font-bold py-2 px-4 rounded flex-1 flex items-center justify-center"
            >
              <span className="animate-spin h-4 w-4 border-t-2 border-b-2 border-white rounded-full mr-2"></span>
              Génération...
            </button>
          ) : (
            hasTokens ? (
              <button
                onClick={generateFactChecking}
                className="bg-zemushover hover:bg-white text-gray-500 font-bold py-2 px-4 rounded flex-1"
              >
                Générer
              </button>
            ) : (
              <button
                disabled
                className="bg-gray-500 text-white font-bold py-2 px-4 rounded flex-1 cursor-not-allowed"
              >
                Pas de tokens
              </button>
            )
          )}
          <button
            onClick={() => setShowPopUpFactChecking(false)}
            className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded flex-1"
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};
